/**
 * 浏览器端 Supabase 客户端 —— 懒加载
 *
 * 设计：
 * - `isSupabaseEnabled` 是 sync env 检查，不导入 supabase-js
 * - `getSupabase()` 第一次被调时才动态 import @supabase/supabase-js
 *   → 主 chunk 不再包含 ~150KB gzip 的 supabase-js
 *   → 只在用户真正访问需要登录的 page 时才下载（/profile / /tracker 等）
 * - 失败 / 未配置时返回 null（同步代码用 isSupabaseEnabled 判断走 fallback）
 * - PKCE 流程，更适合 SPA + OAuth
 */

const url = import.meta.env.VITE_SUPABASE_URL || '';
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

export const isSupabaseEnabled = Boolean(url && anonKey);

let _clientPromise = null;

/**
 * 异步取 Supabase 客户端单例。
 * 未配置时永远 resolve 成 null。
 */
export function getSupabase() {
  if (!isSupabaseEnabled) return Promise.resolve(null);
  if (!_clientPromise) {
    _clientPromise = import('@supabase/supabase-js').then(({ createClient }) =>
      createClient(url, anonKey, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
          flowType: 'pkce',
          storageKey: 'careerops.auth',
        },
      }),
    );
  }
  return _clientPromise;
}

/**
 * 取当前 access_token，用于附加到 /api/* 请求的 Authorization 头
 */
export async function getAccessToken() {
  const sb = await getSupabase();
  if (!sb) return null;
  try {
    const { data } = await sb.auth.getSession();
    return data?.session?.access_token || null;
  } catch {
    return null;
  }
}
